// src/shared/services/sessionManager.ts

export interface SessionConfig {
  timeout: number; // Session timeout in milliseconds (default: 30 minutes)
  warningTime: number; // Warning time before timeout in milliseconds (default: 5 minutes)
  checkInterval: number; // Interval to check for activity in milliseconds (default: 1 minute)
}

export interface SessionEventCallbacks {
  onWarning: () => void;
  onTimeout: () => void;
  onExtended: () => void;
}

class SessionManager {
  private static instance: SessionManager;
  private lastActivityTime: number = Date.now();
  private warningShown: boolean = false;
  private checkTimer: NodeJS.Timeout | null = null;
  // Guarda a referência do handler para conseguir removê-lo depois
  private activityHandler: (() => void) | null = null;
  private config: SessionConfig;
  private callbacks: SessionEventCallbacks | null = null;
  private isEnabled: boolean = false;

  // Default configuration: 60 minute timeout, 10 minute warning
  private defaultConfig: SessionConfig = {
    timeout: 60 * 60 * 1000, // 60 minutes
    warningTime: 10 * 60 * 1000, // 10 minutes before timeout
    checkInterval: 60 * 1000 // Check every minute
  };

  private constructor() {
    this.config = { ...this.defaultConfig };
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Initialize session management with configuration and callbacks
   */
  initialize(callbacks: SessionEventCallbacks, config?: Partial<SessionConfig>): void {
    this.callbacks = callbacks;
    this.config = { ...this.defaultConfig, ...config };
    this.isEnabled = true;
    this.lastActivityTime = Date.now();
    this.warningShown = false;

    // Start session monitoring
    this.startSessionCheck();
    this.setupActivityListeners();
  }

  /**
   * Stop session management (call on logout)
   */
  stop(): void {
    this.isEnabled = false;
    this.clearTimers();
    this.removeActivityListeners();
    this.callbacks = null;
  }

  /**
   * Record user activity (call this on any user interaction)
   */
  recordActivity(): void {
    if (!this.isEnabled) return;

    const now = Date.now();

    this.lastActivityTime = now;

    // Reset warning if it was shown
    if (this.warningShown) {
      this.warningShown = false;
      if (this.callbacks?.onExtended) {
        this.callbacks.onExtended();
      }
    }
  }

  /**
   * Manually extend session (call when user dismisses warning)
   */
  extendSession(): void {
    this.recordActivity();
  }

  /**
   * Get time remaining until session expires
   */
  getTimeRemaining(): number {
    if (!this.isEnabled) return 0;
    
    const timeElapsed = Date.now() - this.lastActivityTime;
    const timeRemaining = this.config.timeout - timeElapsed;
    return Math.max(0, timeRemaining);
  }

  /**
   * Check if session is about to expire (within warning time)
   */
  isAboutToExpire(): boolean {
    return this.getTimeRemaining() <= this.config.warningTime;
  }

  /**
   * Check if session has expired
   */
  isExpired(): boolean {
    return this.getTimeRemaining() <= 0;
  }

  /**
   * Set up event listeners for user activity
   */
  private setupActivityListeners(): void {
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    // Evita listeners duplicados se initialize() for chamado mais de uma vez
    this.removeActivityListeners();

    // Throttle por timestamp (leading edge): registra a atividade na hora,
    // e não 5s depois como no setTimeout anterior.
    let lastRecorded = 0;
    const throttledRecordActivity = () => {
      const now = Date.now();
      if (now - lastRecorded < 5000) return;
      lastRecorded = now;
      this.recordActivity();
    };

    this.activityHandler = throttledRecordActivity;
    events.forEach(event => {
      document.addEventListener(event, throttledRecordActivity, true);
    });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    const events = [
      'mousedown',
      'mousemove',
      'keypress', 
      'scroll',
      'touchstart',
      'click',
      'keydown'
    ];

    if (!this.activityHandler) return;
    events.forEach(event => {
      document.removeEventListener(event, this.activityHandler!, true);
    });
    this.activityHandler = null;
  }

  /**
   * Start the session check timer
   */
  private startSessionCheck(): void {
    this.clearTimers();
    
    this.checkTimer = setInterval(() => {
      this.checkSession();
    }, this.config.checkInterval);
  }

  /**
   * Check session status and trigger warnings/timeout
   */
  private checkSession(): void {
    if (!this.isEnabled || !this.callbacks) return;

    // Session expired
    if (this.isExpired()) {
      this.callbacks.onTimeout();
      this.stop();
      return;
    }

    // Show warning if approaching expiration and warning not already shown
    if (this.isAboutToExpire() && !this.warningShown) {
      this.warningShown = true;
      this.callbacks.onWarning();
    }
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
  }

  /**
   * Get current session status for debugging
   */
  getSessionStatus(): any {
    if (!this.isEnabled) {
      return { status: 'disabled' };
    }

    const timeRemaining = this.getTimeRemaining();
    const minutesRemaining = Math.ceil(timeRemaining / 60000);

    return {
      status: 'active',
      timeRemaining: timeRemaining,
      minutesRemaining: minutesRemaining,
      isAboutToExpire: this.isAboutToExpire(),
      isExpired: this.isExpired(),
      warningShown: this.warningShown,
      lastActivityTime: new Date(this.lastActivityTime).toISOString()
    };
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();